<template>
  <div v-if="show" class="modal-overlay">
    <div class="modal-container">
      <!-- Header -->
      <div class="modal-header">
        <h2 class="modal-title">New Journal Voucher</h2>
        <button class="close-btn" @click="$emit('close')">×</button>
      </div>

      <!-- Body -->
      <div class="modal-body">
        <!-- Voucher Info -->
        <div class="form-row">
          <div class="form-group">
            <label class="form-label">Voucher Number</label>
            <input
              v-model="form.voucherNumber"
              type="text"
              class="form-input"
              placeholder="Enter voucher number"
            />
          </div>

          <div class="form-group">
            <label class="form-label">Voucher Date</label>
            <input
              v-model="form.voucherDate"
              type="date"
              class="form-input"
            />
          </div>
        </div>

        <div class="form-group full-width">
          <label class="form-label">Description</label>
          <textarea
            v-model="form.description"
            class="form-textarea"
            rows="3"
            placeholder="Enter voucher description"
          ></textarea>
        </div>

        <!-- Journal Entries -->
        <div class="entries-section">
          <div class="entries-header">
            <h3 class="entries-title">Journal Entries</h3>
            <button class="add-row-btn" @click="addEntry">
              <span class="plus-icon">+</span> Add Row
            </button>
          </div>

          <div class="entries-table">
            <div class="table-header">
              <div class="table-cell">Account</div>
              <div class="table-cell">Party Type</div>
              <div class="table-cell">Party</div>
              <div class="table-cell">Debit</div>
              <div class="table-cell">Credit</div>
              <div class="table-cell"></div>
            </div>

            <div
              v-for="(entry, index) in entries"
              :key="index"
              class="table-row"
            >
              <!-- Account -->
              <select v-model="entry.chartOfAccountID" class="table-input">
                <option value="">Select Account</option>
                <option
                  v-for="acc in accounts"
                  :key="acc.id"
                  :value="acc.id"
                >
                  {{ acc.name }}
                </option>
              </select>

              <!-- Party Type -->
              <select
                v-model="entry.selectedPartyType"
                class="table-input"
                @change="entry.partyID = ''"
              >
                <option value="">Select Type</option>
                <option
                  v-for="type in partyTypes"
                  :key="type.id"
                  :value="type"
                >
                  {{ type.type }}
                </option>
              </select>

              <!-- Party -->
              <select v-model="entry.partyID" class="table-input">
                <option value="">Select Party</option>
                <option
                  v-for="party in entry.selectedPartyType?.parties || []"
                  :key="party.id"
                  :value="party.id"
                >
                  {{ party.name }}
                </option>
              </select>

              <!-- Debit -->
              <input
                type="number"
                v-model.number="entry.debit"
                class="table-input"
              />

              <!-- Credit -->
              <input
                type="number"
                v-model.number="entry.credit"
                class="table-input"
              />

              <!-- Delete -->
              <button
                class="delete-btn"
                @click="removeEntry(index)"
                :disabled="entries.length <= 2"
              >
                🗑️
              </button>
            </div>

            <!-- Totals -->
            <div class="table-row total-row">
              <div></div>
              <div></div>
              <div style="text-align:right">Total:</div>
              <div>{{ totalDebit.toFixed(2) }}</div>
              <div>{{ totalCredit.toFixed(2) }}</div>
              <div></div>
            </div>
          </div>

          <div v-if="errorMessage" class="error-message">
            {{ errorMessage }}
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="modal-footer">
        <button class="btn-cancel" @click="$emit('close')">
          Cancel
        </button>
        <button type="submit" class="btn-create" @click="createVoucher" :disabled="loading">
              <span v-if="loading">Creating...</span>
              <span v-else>Create Voucher</span>
        </button>
      </div>
    </div>
  </div>
</template>
<script>
import { mapActions, mapState } from 'vuex'

export default {
  name: 'NewJournalVoucherModal',

  props: {
    show: {
      type: Boolean,
      required: true
    },
    accounts: {
      type: Array,
      required: true
    }
  },

  data() {
    return {
      partyTypes: [],
      isLoading: false,
      errorMessage: '',

      form: {
        voucherDate: new Date().toISOString().substring(0, 10),
        voucherNumber: '',
        description: ''
      },

      entries: []
    }
  },

  computed: {
    ...mapState('accounting', ['loading']),
    totalDebit() {
      return this.entries.reduce((sum, e) => sum + (e.debit || 0), 0)
    },
    totalCredit() {
      return this.entries.reduce((sum, e) => sum + (e.credit || 0), 0)
    }
  },

  watch: {
    show(val) {
      if (val) {
        this.resetForm()
        this.getParties()
      }
    }
  },

  methods: {
    ...mapActions('party', ['fetchParties']),
    ...mapActions('accounting', ['AddJournalVoucher']),
    async getParties() {
      const res = await this.fetchParties()
      this.partyTypes = res
    },

    addEntry() {
      this.entries.push({
        chartOfAccountID: '',
        selectedPartyType: null,
        partyID: '',
        debit: 0,
        credit: 0
      })
    },

    removeEntry(index) {
      if (this.entries.length > 2) {
        this.entries.splice(index, 1)
      }
    },

    async createVoucher() {
      if (this.entries.length < 2) {
        this.errorMessage = 'At least two journal entries are required.'
        return
      }
      try {
      this.loading = true
      const payload = {
        voucherDate: new Date(this.form.voucherDate).toISOString(),
        voucherNumber: this.form.voucherNumber,
        description: this.form.description,
        journalEntries: this.entries.map(e => ({
          chartOfAccountID: parseInt(e.chartOfAccountID),
          partyID: e.partyID ? parseInt(e.partyID) : 0,
          debit: e.debit || 0,
          credit: e.credit || 0
        }))
      }
      await this.AddJournalVoucher(payload)
        this.$emit('close')
      } catch (err) {
        console.error(err)
      } finally {
        this.loading = false
      }
    },

    resetForm() {
      this.form = {
        voucherDate: new Date().toISOString().substring(0, 10),
        voucherNumber: '',
        description: ''
      }

      this.entries = []
      this.addEntry()
      this.addEntry() // ✅ minimum 2 rows
      this.errorMessage = ''
    }
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.modal-container {
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 900px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
}

/* Modal Header */
.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px;
  border-bottom: 1px solid #e5e7eb;
}

.modal-title {
  font-size: 20px;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
}

.close-btn {
  background: none;
  border: none;
  font-size: 28px;
  color: #6b7280;
  cursor: pointer;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s ease;
}

.close-btn:hover {
  color: #1f2937;
}

/* Modal Body */
.modal-body {
  padding: 24px;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-group.full-width {
  grid-column: 1 / -1;
}

.form-label {
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 8px;
}

.form-input,
.form-textarea {
  padding: 10px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 14px;
  font-family: inherit;
  transition: border-color 0.2s ease;
}

.form-input:focus,
.form-textarea:focus {
  outline: none;
  border-color: #2563eb;
}

.form-textarea {
  resize: vertical;
}

.form-input::placeholder,
.form-textarea::placeholder {
  color: #9ca3af;
}

/* Entries Section */
.entries-section {
  margin-top: 28px;
}

.entries-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.entries-title {
  font-size: 16px;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
}

.add-row-btn {
  background: none;
  border: none;
  color: #2563eb;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: color 0.2s ease;
}

.add-row-btn:hover {
  color: #1d4ed8;
}

.plus-icon {
  font-size: 16px;
}

/* Entries Table */
.entries-table {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
}

.table-header {
  display: grid;
  grid-template-columns: 2fr 1fr 1.5fr 1fr 1fr 40px;
  gap: 0;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
  padding: 12px;
}

.table-row {
  display: grid;
  grid-template-columns: 2fr 1fr 1.5fr 1fr 1fr 40px;
  gap: 0;
  padding: 12px;
  border-bottom: 1px solid #e5e7eb;
  align-items: center;
}

.table-row.total-row {
  background: #f9fafb;
  border-bottom: none;
  font-weight: 600;
  color: #1f2937;
}

.table-cell {
  font-size: 13px;
  color: #6b7280;
  font-weight: 600;
  text-align: left;
}

.table-row:not(.table-header) .table-cell {
  color: inherit;
  font-weight: normal;
}

.table-row.total-row .table-cell {
  font-weight: 600;
  color: #1f2937;
}

.account-cell {
  flex: 2;
}

.party-type-cell {
  flex: 1;
}

.party-cell {
  flex: 1.5;
}

.amount-cell {
  flex: 1;
  text-align: right;
}

.action-cell {
  flex: 0 0 40px;
  text-align: center;
}

.table-input {
  width: 100%;
  padding: 8px 10px;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  font-size: 13px;
  font-family: inherit;
  transition: border-color 0.2s ease;
}

.table-input:focus {
  outline: none;
  border-color: #2563eb;
}

.total-value {
  font-weight: 700;
  color: #1f2937;
}

.delete-btn {
  background: none;
  border: none;
  font-size: 16px;
  cursor: pointer;
  padding: 4px 8px;
  transition: opacity 0.2s ease;
}

.delete-btn:hover {
  opacity: 0.7;
}

/* Modal Footer */
.modal-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  padding: 20px 24px;
  border-top: 1px solid #e5e7eb;
  background: #f9fafb;
}

.btn-cancel {
  background: white;
  border: 1px solid #e5e7eb;
  color: #1f2937;
  padding: 10px 24px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease;
}

.btn-cancel:hover {
  background: #f3f4f6;
}

.btn-create {
  background: #2563eb;
  color: white;
  border: none;
  padding: 10px 24px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease;
}

.btn-create:hover {
  background: #1d4ed8;
}

.btn-create:disabled {
  background: #9ca3af;
  cursor: not-allowed;
  opacity: 0.6;
}

.error-message {
  background: #fee2e2;
  color: #991b1b;
  padding: 12px 16px;
  border-radius: 6px;
  font-size: 14px;
  margin-top: 16px;
  border-left: 4px solid #dc2626;
}

@media (max-width: 768px) {
  .modal-overlay {
    padding: 16px;
  }

  .modal-content {
    max-width: 100%;
  }

  .form-row {
    grid-template-columns: 1fr;
  }

  .table-header,
  .table-row {
    grid-template-columns: 1fr 1fr 1fr 40px;
  }

  .party-cell {
    display: none;
  }
}
</style>
